import { useState } from "react";

import { Option } from "./type";

type UseFilterableMultiSelectProps = {
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
};

export const useFilterableMultiSelect = ({ options, onChange, value }: UseFilterableMultiSelectProps) => {
  const [selectedPath, setSelectedPath] = useState<Option[]>([]);

  const handleOptionSelect = (option: Option, level: number) => {
    const newPath = [...selectedPath.slice(0, level), option];
    setSelectedPath(newPath);
  };

  const handleValueChange = (option: Option) => {
    if (!option.options) {
      const newValue = value.includes(option.value)
        ? value.filter((v) => v !== option.value)
        : [...value, option.value];
      onChange(newValue);
      return;
    }

    const values = getValues(option.options);

    const newValue = values.filter((item) => value.includes(item))?.length
      ? value.filter((v) => !values.includes(v))
      : [...value, ...values];

    onChange(newValue);
  };

  const getValueOptions = (targetOptions: Option[]): Option[] =>
    targetOptions.flatMap((option) => {
      if (!option.options) {
        return {
          value: option.value,
          label: option.label,
        };
      }
      return getValueOptions(option.options);
    });

  const getValues = (targetOptions: Option[]): string[] => getValueOptions(targetOptions).map((option) => option.value);

  const isChecked = (option: Option) => {
    if (!option.options) {
      return value.includes(option.value);
    }

    const values = getValues(option.options);
    const result = values.filter((item) => value.includes(item));

    return result?.length === values?.length ? true : result?.length === 0 ? false : "indeterminate";
  };

  const getValueLabel = (targetValue: string): string => {
    // 親要素からvalueに辿り着くまでのlabelを/でつないで返す
    const findLabel = (findTargetOptions: Option[], findTargetValue: string): string => {
      for (const findTargetOption of findTargetOptions) {
        if (findTargetOption.value === findTargetValue) {
          return findTargetOption.label;
        }

        if (findTargetOption.options) {
          const label = findLabel(findTargetOption.options, findTargetValue);
          if (label) {
            return `${findTargetOption.label}/${label}`;
          }
        }
      }
      return "";
    };
    return findLabel(options, targetValue);
  };

  return {
    selectedPath,
    handleOptionSelect,
    handleValueChange,
    isChecked,
    getValueLabel,
  };
};
